// Float-returning sibling of useCountUp. Same ease-out cubic curve,
// same reduced-motion early-return, but does NOT round the value
// internally so callers can format with .toFixed(1) etc. for decimal
// displays like "5.2 días" or "+14.2%".
//
// Why a separate hook instead of an option flag on useCountUp:
// useCountUp has 8 production consumers (gastos dashboards, list
// summaries) that expect integer output. Adding an option without
// breaking those would mean wrapping every existing call site in
// Math.round — invasive. New hook = additive, zero risk to existing
// callers, intent crystal clear at the new call sites.
//
// Cold-render fallback: if RAF never fires (frozen iframe, hidden
// tab, throttled background) the hook returns the target value
// immediately on first render after mount via the setV(target) in
// the reduced-motion branch. Effect runs synchronously after mount;
// even if subsequent RAF callbacks never fire, the screen shows the
// correct final value, not 0.

'use client';

import { useEffect, useState } from 'react';

export function useCountUpFloat(target: number, durationMs = 900, delay = 0): number {
  const [v, setV] = useState(target);

  useEffect(() => {
    const reduced =
      typeof window !== 'undefined' &&
      window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduced || target === 0) {
      setV(target);
      return;
    }

    // Reset to 0 INSIDE the first RAF so the cold-render path (no
    // RAF) leaves the previously-rendered final value on screen.
    // That's the "Stripe-style entrance" trick: appear, then play.
    setV(0);
    let raf = 0;
    let start: number | undefined;
    const tick = (t: number) => {
      if (start === undefined) start = t;
      const elapsed = t - start - delay;
      if (elapsed < 0) {
        raf = requestAnimationFrame(tick);
        return;
      }
      const p = Math.min(1, elapsed / durationMs);
      const eased = 1 - Math.pow(1 - p, 3);
      setV(target * eased);
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, durationMs, delay]);

  return v;
}
